import { PlaylistModel } from './model';
import { APIController } from '../api/controller';
import { action, autorun, IReactionDisposer, observable, runInAction } from 'mobx';
import { EnvModel } from '../env/model';
import { UserModel } from '../user/model';
import { BugsnagController } from '../bugsnag/controller';

export class PlaylistController {
  constructor(
    private model: PlaylistModel,
    private env: EnvModel,
    private user: UserModel,
    private api: APIController,
    private bugsnag: BugsnagController
  ) {
    this._disposers = [];

    this.model.sync[this.env.playlistFavourites] = observable([]);
    this.model.tracks[this.env.playlistFavourites] = observable([]);
    this.model.removed[this.env.playlistFavourites] = observable([]);

    if (!this.env.isServer) {
      let userId: string;
      this._disposers.push(autorun(() => {
        if (user.user && user.user.identity && user.user.identity !== userId) {
          this.fetchFavourites();
          userId = user.user.identity;
        }
      }));
    }
  }

  private _disposers: IReactionDisposer[];

  dispose = () => {
    this._disposers.forEach(dispose => dispose());
  };

  @action
  addToPlaylist = async (playlist: string, track: string) => {
    if (!this.user.user) return;

    this.setSync(playlist, track, true);

    try {
      await this.api.playlist.addTrackToPlaylist(this.user.user.identity, playlist, track);

      this.setActive(playlist, track, true);
    } catch(e) {
      this.bugsnag.notifyException(e);
    } finally {
      this.setSync(playlist, track, false);
    }
  };

  @action
  removeFromPlaylist = async (playlist: string, track: string) => {
    if (!this.user.user) return;

    this.setSync(playlist, track, true);

    try {
      await this.api.playlist.removeTrackFromPlaylist(this.user.user.identity, playlist, track);

      this.setActive(playlist, track, false);
    } catch(e) {
      this.bugsnag.notifyException(e);
    } finally {
      this.setSync(playlist, track, false);
    }
  };

  @action
  favourite = async (track: string) => {
    return this.addToPlaylist(this.env.playlistFavourites, track);
  };

  @action
  unfavourite = async (track: string) => {
    return this.removeFromPlaylist(this.env.playlistFavourites, track);
  };

  @action
  fetchFavourites = async () => {
    if (!this.user.user) return;

    try {
      const tracks = await this.api.playlist.getTracks(this.user.user.identity, this.env.playlistFavourites);

      runInAction(() => {
        this.model.tracks[this.env.playlistFavourites].splice(
          0,
          this.model.tracks[this.env.playlistFavourites].length,
          ...tracks.data.map(t => t.identity)
        );
      })
    } catch(e) {
      this.bugsnag.notifyException(e);
    }
  };

  @action
  private setSync = (playlist: string, track: string, sync: boolean) => {
    if (!this.model.sync[playlist]) {
      this.model.sync[playlist] = observable([]);
    }

    if (sync) {
      this.model.sync[playlist].push(track);
    } else {
      const index = this.model.sync[playlist].indexOf(track);

      if (index !== -1) {
        this.model.sync[playlist].splice(index, 1);
      }
    }
  };

  @action
  private setActive = (playlist: string, track: string, active: boolean) => {
    if (!this.model.tracks[playlist]) {
      this.model.tracks[playlist] = observable([]);
    }

    if (!this.model.removed[playlist]) {
      this.model.removed[playlist] = observable([]);
    }

    if (active) {
      this.model.tracks[playlist].push(track);

      const index = this.model.removed[playlist].indexOf(track);

      if (index !== -1) {
        this.model.removed[playlist].splice(index, 1);
      }
    } else {
      this.model.removed[playlist].push(track);

      const index = this.model.tracks[playlist].indexOf(track);

      if (index !== -1) {
        this.model.tracks[playlist].splice(index, 1);
      }
    }
  };
}